/**
 * Dashboard page.
 *
 * Phase 16: Product Intelligence UX.
 *
 * Zones:
 *   A — Hero / page header (greeting, org, plan)
 *   B — Posture hero: org-wide score + recommended-action panel
 *   C — Priority callout (critical / unscanned / stale alerts)
 *   D — Key metric stat cards
 *   E — Plan usage
 *   F — Recent scan activity table (with Score column)
 *   G — Your sites table
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api-client';
import { formatDate, formatNumber } from '../i18n/formatters';
import { getPlanLabel } from '../lib/plans';
import type {
  Alert,
  FleetHealthSummary,
  Organisation,
  SiteHealthState,
} from '@accestra/types';
import {
  estimateAccessibilityScore,
  getScoreVariant,
} from '../lib/accessibility-score';
import Card from '../components/ui/Card';
import Badge, { BadgeVariant } from '../components/ui/Badge';
import Container from '../components/ui/Container';
import EmptyState from '../components/ui/EmptyState';
import StatCard from '../components/ui/StatCard';
import AlertBanner from '../components/ui/AlertBanner';
import SectionHeader from '../components/ui/SectionHeader';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import ScoreCard from '../components/ui/ScoreCard';
import RecommendationPanel from '../components/ui/RecommendationPanel';
import { subscribeToWorkspaceDataChanges } from '../lib/workspace-data-events';

// -------------------------------------------------------------------
// Types
// -------------------------------------------------------------------

interface SiteWithConfig {
  id: string;
  name: string;
  baseUrl: string;
  status: string;
  scanConfig: { wcagLevel: string; maxPages: number } | null;
}

interface Scan {
  id: string;
  siteId: string;
  status: string;
  scanType: string;
  totalIssues: number;
  criticalIssues: number;
  pagesScanned: number;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

interface SitesResponse {
  sites: SiteWithConfig[];
  planInfo?: { plan: string; sitesUsed: number; sitesLimit: number };
}

interface RecentScansData {
  siteId: string;
  siteName: string;
  scan: Scan | null;
}

interface PlanLimits {
  maxSites: number;
  maxScansPerMonth: number;
  maxPagesPerScan: number;
  scheduledScansEnabled: boolean;
  complianceExportEnabled: boolean;
}

interface UsageData {
  plan: string;
  limits: PlanLimits;
  usage: {
    sitesCount: number;
    scansThisMonth: number;
  };
}

interface OrganisationResponse {
  organisation: Organisation;
}

interface ScanSummaryResponse {
  scan: {
    id: string;
    status: string;
    pagesScanned: number;
    totalIssues: number;
    criticalIssues: number;
    completedAt: string | null;
  };
  site: {
    id: string;
    name: string;
    baseUrl: string;
  };
  issueCounts: {
    critical: number;
    serious: number;
    moderate: number;
    minor: number;
  };
  workflowSummary: {
    open: number;
    acknowledged: number;
    inProgress: number;
    resolved: number;
    falsePositive: number;
    suppressed: number;
  };
  topIssues: Array<{
    issueId: string;
    code: string;
    title: string;
    impact: string;
    occurrences: number;
    affectedPages: number;
    workflowState: string;
    suppressedCount: number;
    samplePageUrl: string | null;
    samplePageEvidenceId: string | null;
  }>;
  deepLinks: {
    scanDetailPath: string;
    siteDetailPath: string;
    siteCompliancePath: string;
  };
}

function healthStateVariant(state: SiteHealthState): BadgeVariant {
  switch (state) {
    case 'auth_revalidation_needed':
    case 'unresolved_critical_issues_present':
      return 'danger';
    case 'regression_detected':
    case 'attention_needed':
      return 'warning';
    case 'healthy':
    default:
      return 'success';
  }
}

function healthStateLabel(state: SiteHealthState, t: TFunction<'dashboard'>): string {
  switch (state) {
    case 'auth_revalidation_needed': return t('fleetHealth.state.auth_revalidation_needed');
    case 'regression_detected': return t('fleetHealth.state.regression_detected');
    case 'unresolved_critical_issues_present': return t('fleetHealth.state.unresolved_critical_issues_present');
    case 'attention_needed': return t('fleetHealth.state.attention_needed');
    case 'healthy':
    default:
      return t('fleetHealth.state.healthy');
  }
}

function fleetSummaryLabel(summary: FleetHealthSummary | null, t: TFunction<'dashboard'>): string {
  if (!summary) {
    return t('sections.yourSitesSubtitle');
  }
  if (summary.counts.authRevalidationNeeded > 0) {
    return t('fleetHealth.summary.authRevalidation', { count: summary.counts.authRevalidationNeeded });
  }
  if (summary.counts.unresolvedCriticalIssuesPresent > 0) {
    return t('fleetHealth.summary.criticalIssues', { count: summary.counts.unresolvedCriticalIssuesPresent });
  }
  if (summary.counts.regressionDetected > 0) {
    return t('fleetHealth.summary.regressionDetected', { count: summary.counts.regressionDetected });
  }
  if (summary.counts.attentionNeeded > 0) {
    return t('fleetHealth.summary.attentionNeeded', { count: summary.counts.attentionNeeded });
  }
  return t('fleetHealth.summary.healthy');
}

function fleetSiteSummaryLabel(site: FleetHealthSummary['sites'][number], t: TFunction<'dashboard'>): string {
  if (site.authState === 'auth_failed') {
    return t('fleetHealth.siteSummary.authFailed');
  }
  if (site.authState === 'reauth_required') {
    return t('fleetHealth.siteSummary.reauthRequired');
  }
  if (site.openCriticalIssues > 0) {
    return t('fleetHealth.siteSummary.criticalIssues', { count: site.openCriticalIssues });
  }
  if (site.latestRegressionState === 'regressed') {
    return t('fleetHealth.siteSummary.regressionDetected');
  }
  if (site.evidenceCleanupCandidates > 0) {
    return t('fleetHealth.siteSummary.evidenceCleanup', { count: site.evidenceCleanupCandidates });
  }
  if (site.state === 'healthy') {
    return t('fleetHealth.siteSummary.healthy');
  }
  return t('fleetHealth.siteSummary.attentionNeeded');
}

function formatWorkflowState(state: string, t: TFunction<'dashboard'>): string {
  switch (state) {
    case 'acknowledged': return t('workflowStates.acknowledged');
    case 'in_progress': return t('workflowStates.in_progress');
    case 'resolved': return t('workflowStates.resolved');
    case 'false_positive': return t('workflowStates.false_positive');
    case 'open':
    default:
      return t('workflowStates.open');
  }
}

function alertSeverityLabel(severity: string, t: TFunction<['dashboard', 'common', 'alerts']>): string {
  switch (severity) {
    case 'critical':
      return t('alerts:severity.critical');
    case 'warning':
      return t('alerts:severity.warning');
    case 'info':
    default:
      return t('alerts:severity.info');
  }
}

// -------------------------------------------------------------------
// Design tokens
// -------------------------------------------------------------------

const C = {
  page:   'var(--acc-color-page)',
  text:   'var(--acc-color-text)',
  muted:  'var(--acc-color-text-muted)',
  soft:   'var(--acc-color-surface-muted)',
  border: 'var(--acc-color-border)',
  link:   'var(--acc-color-primary)',
  success: 'var(--acc-color-success)',
  successSoft: 'var(--acc-color-success-soft)',
  warning: 'var(--acc-color-warning)',
  danger: 'var(--acc-color-danger)',
};

// -------------------------------------------------------------------
// Status → Badge variant
// -------------------------------------------------------------------

function statusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'completed':               return 'success';
    case 'completed_with_failures': return 'warning';
    case 'running':                 return 'warning';
    case 'queued':                  return 'info';
    case 'failed':                  return 'danger';
    case 'auth_failed':             return 'danger';
    default:                        return 'neutral';
  }
}

function statusLabel(status: string, t: TFunction<'dashboard'>): string {
  if (status in {
    completed: true,
    completed_with_failures: true,
    auth_failed: true,
    running: true,
    queued: true,
    failed: true,
  }) {
    return t(`statuses.${status}` as const);
  }
  return status.replace(/_/g, ' ');
}

// -------------------------------------------------------------------
// QuotaBar
// -------------------------------------------------------------------

function QuotaBar({ used, limit, label, testId }: { used: number; limit: number; label: string; testId?: string }) {
  const { t } = useTranslation('dashboard');
  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const nearLimit = pct >= 80;
  const atLimit = pct >= 100;
  const barColor = atLimit
    ? 'var(--acc-color-danger)'
    : nearLimit
      ? 'var(--acc-color-warning)'
      : 'var(--acc-color-primary)';

  return (
    <div style={{ marginBottom: 16 }} data-testid={testId}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
        <span style={{ color: C.muted, fontWeight: 500 }}>{label}</span>
        <span style={{ fontWeight: 700, color: atLimit ? 'var(--acc-color-danger)' : nearLimit ? 'var(--acc-color-warning)' : C.text }}>
          {used} <span style={{ fontWeight: 400, color: C.muted }}>/ {limit}</span>
        </span>
      </div>
      <div style={{ height: 6, background: C.soft, borderRadius: 999, overflow: 'hidden' }}>
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: barColor,
            borderRadius: 3,
            transition: 'width 0.3s',
          }}
        />
      </div>
      {atLimit && (
        <div style={{ fontSize: 12, color: 'var(--acc-color-danger)', marginTop: 5, fontWeight: 500 }}>
          {t('planUsage.limitReached')}
        </div>
      )}
      {nearLimit && !atLimit && (
        <div style={{ fontSize: 12, color: 'var(--acc-color-warning)', marginTop: 5 }}>
          {t('planUsage.approachingLimit')}
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------
// Recommendation helper
// -------------------------------------------------------------------

interface Recommendation {
  icon: string;
  title: string;
  body: string;
  variant: 'danger' | 'warning' | 'info' | 'neutral';
  to?: string;
  cta?: string;
}

function deriveRecommendation(
  t: TFunction<'dashboard'>,
  atPlanLimit: boolean,
  scansAtLimit: boolean,
  totalCritical: number,
  sitesWithCritical: RecentScansData[],
  unscannedSites: RecentScansData[],
  staleSites: RecentScansData[],
  activeScans: RecentScansData[],
  sites: SiteWithConfig[],
): Recommendation | null {
  if (sites.length === 0) return null;

  if (atPlanLimit) {
    return {
      icon: '🚧',
      title: t('recommendation.planLimitTitle', {
        kind: scansAtLimit ? t('planUsage.scansThisMonth') : t('planUsage.sites'),
      }),
      body: t('recommendation.planLimitBody'),
      variant: 'danger',
    };
  }

  if (totalCritical > 0) {
    const worst = sitesWithCritical[0];
    return {
      icon: '🔴',
      title: t('recommendation.criticalTitle', { count: totalCritical }),
      body: t('recommendation.criticalBody', { siteName: worst.siteName }),
      variant: 'danger',
      to: `/sites/${worst.siteId}`,
      cta: t('recommendation.criticalCta', { siteName: worst.siteName }),
    };
  }

  if (unscannedSites.length > 0) {
    const first = unscannedSites[0];
    return {
      icon: '🔍',
      title: t('recommendation.firstScanTitle', { siteName: first.siteName }),
      body: t('recommendation.firstScanBody', { count: unscannedSites.length }),
      variant: 'warning',
      to: `/sites/${first.siteId}`,
      cta: t('recommendation.firstScanCta'),
    };
  }

  if (activeScans.length > 0) {
    return {
      icon: '⏳',
      title: t('recommendation.activeScansTitle', { count: activeScans.length }),
      body: t('recommendation.activeScansBody'),
      variant: 'info',
    };
  }

  if (staleSites.length > 0) {
    const first = staleSites[0];
    return {
      icon: '🕐',
      title: t('recommendation.staleTitle', { siteName: first.siteName }),
      body: t('recommendation.staleBody', { count: staleSites.length }),
      variant: 'neutral',
      to: `/sites/${first.siteId}`,
      cta: t('recommendation.staleCta'),
    };
  }

  return {
    icon: '✓',
    title: t('recommendation.allGoodTitle'),
    body: t('recommendation.allGoodBody'),
    variant: 'info',
  };
}

// -------------------------------------------------------------------
// Page component
// -------------------------------------------------------------------

export default function DashboardPage() {
  const { t } = useTranslation(['dashboard', 'common', 'alerts', 'reports']);
  const { user, org } = useAuth();
  const navigate = useNavigate();
  type OnboardingFeedback = {
    message: string;
    variant: 'success' | 'warning';
  };
  const [sitesData, setSitesData] = useState<SitesResponse | null>(null);
  const [recentScans, setRecentScans] = useState<RecentScansData[]>([]);
  const [usageData, setUsageData] = useState<UsageData | null>(null);
  const [recentAlerts, setRecentAlerts] = useState<Alert[]>([]);
  const [fleetHealth, setFleetHealth] = useState<FleetHealthSummary | null>(null);
  const [workspaceOrg, setWorkspaceOrg] = useState<Organisation | null>(null);
  const [firstScanSummary, setFirstScanSummary] = useState<ScanSummaryResponse | null>(null);
  const [onboardingFeedback, setOnboardingFeedback] = useState<OnboardingFeedback | null>(null);
  const [onboardingVisible, setOnboardingVisible] = useState(true);
  const [savingOnboarding, setSavingOnboarding] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadDashboard = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;

    if (!org?.slug) {
      setLoading(false);
      return;
    }

    if (!silent) {
      setLoading(true);
    }

    try {
      const [sitesRes, usageRes, alertsRes, fleetHealthRes, orgRes] = await Promise.allSettled([
        api.get<SitesResponse>('/sites'),
        api.get<UsageData>('/orgs/me/usage'),
        api.get<{ alerts: Alert[]; unreadCount: number }>('/alerts?pageSize=5'),
        api.get<{ summary: FleetHealthSummary }>('/sites/health-summary'),
        api.get<OrganisationResponse>(`/orgs/${org.slug}`),
      ]);

      if (sitesRes.status === 'fulfilled') setSitesData(sitesRes.value);
      if (usageRes.status === 'fulfilled') setUsageData(usageRes.value);
      if (alertsRes.status === 'fulfilled') setRecentAlerts(alertsRes.value.alerts ?? []);
      if (fleetHealthRes.status === 'fulfilled') setFleetHealth(fleetHealthRes.value.summary ?? null);
      if (orgRes.status === 'fulfilled') setWorkspaceOrg(orgRes.value.organisation);

      const sites = sitesRes.status === 'fulfilled' ? (sitesRes.value.sites ?? []) : [];
      const scanPromises = sites.slice(0, 10).map(async (site) => {
        try {
          const scansRes = await api.get<{ scans: Scan[] }>(
            `/sites/${site.id}/scans?pageSize=1`,
          );
          return { siteId: site.id, siteName: site.name, scan: scansRes.scans?.[0] ?? null };
        } catch {
          return { siteId: site.id, siteName: site.name, scan: null };
        }
      });

      const results = await Promise.all(scanPromises);
      setRecentScans(results);

      const latestCompletedScan = results
        .map((entry) => entry.scan)
        .filter((scan): scan is Scan => scan != null && (
          scan.status === 'completed' || scan.status === 'completed_with_failures'
        ))
        .sort((left, right) => {
          const leftTime = new Date(left.completedAt ?? left.createdAt).getTime();
          const rightTime = new Date(right.completedAt ?? right.createdAt).getTime();
          return rightTime - leftTime;
        })[0] ?? null;

      if (latestCompletedScan) {
        try {
          const summary = await api.get<ScanSummaryResponse>(`/reports/scans/${latestCompletedScan.id}/summary`);
          setFirstScanSummary(summary);
        } catch {
          setFirstScanSummary(null);
        }
      } else {
        setFirstScanSummary(null);
      }
    } catch {
      // Dashboard degrades gracefully
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [org?.slug]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    return subscribeToWorkspaceDataChanges(() => {
      void loadDashboard({ silent: true });
    });
  }, [loadDashboard]);

  useEffect(() => {
    const dismissed = Boolean(workspaceOrg?.settings.onboarding?.dismissedAt);
    const completed = Boolean(workspaceOrg?.settings.onboarding?.completedAt);
    setOnboardingVisible(!dismissed || completed);
  }, [
    workspaceOrg?.id,
    workspaceOrg?.settings.onboarding?.dismissedAt,
    workspaceOrg?.settings.onboarding?.completedAt,
  ]);

  // -------------------------------------------------------------------
  // Derived state
  // -------------------------------------------------------------------

  const sites = sitesData?.sites ?? [];
  const planInfo = sitesData?.planInfo;
  const planName = getPlanLabel(usageData?.plan ?? planInfo?.plan);

  const totalCritical = recentScans.reduce((sum, r) => sum + (r.scan?.criticalIssues ?? 0), 0);
  const sitesWithCritical = recentScans.filter((r) => (r.scan?.criticalIssues ?? 0) > 0);
  const activeScans = recentScans.filter(
    (r) => r.scan?.status === 'running' || r.scan?.status === 'queued'
  );
  const unscannedSites = recentScans.filter((r) => r.scan === null);

  useEffect(() => {
    if (activeScans.length === 0) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void loadDashboard({ silent: true });
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [activeScans.length, loadDashboard]);

  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const staleSites = recentScans.filter((r) => {
    if (!r.scan) return false;
    const scanDate = new Date(r.scan.completedAt ?? r.scan.createdAt);
    return scanDate < thirtyDaysAgo && r.scan.status === 'completed';
  });

  const scansAtLimit =
    usageData != null &&
    usageData.usage.scansThisMonth >= usageData.limits.maxScansPerMonth;
  const sitesAtLimit =
    usageData != null &&
    usageData.usage.sitesCount >= usageData.limits.maxSites;
  const atPlanLimit = scansAtLimit || sitesAtLimit;

  // Org-wide score: average estimated score across sites that have a completed scan
  const scannedResults = recentScans.filter(
    (r) =>
      r.scan !== null &&
      (r.scan.status === 'completed' || r.scan.status === 'completed_with_failures')
  );
  const orgScore =
    scannedResults.length > 0
      ? Math.round(
          scannedResults.reduce(
            (sum, r) =>
              sum +
              estimateAccessibilityScore(
                r.scan!.criticalIssues ?? 0,
                r.scan!.totalIssues ?? 0
              ),
            0
          ) / scannedResults.length
        )
      : null;

  const recommendation = loading
    ? null
    : deriveRecommendation(
        t,
        atPlanLimit,
        scansAtLimit,
        totalCritical,
        sitesWithCritical,
        unscannedSites,
        staleSites,
        activeScans,
        sites,
      );

  const latestCompletedScan = useMemo(() => {
    return recentScans
      .map((entry) => entry.scan)
      .filter((scan): scan is Scan => scan != null && (
        scan.status === 'completed' || scan.status === 'completed_with_failures'
      ))
      .sort((left, right) => {
        const leftTime = new Date(left.completedAt ?? left.createdAt).getTime();
        const rightTime = new Date(right.completedAt ?? right.createdAt).getTime();
        return rightTime - leftTime;
      })[0] ?? null;
  }, [recentScans]);

  const onboarding = workspaceOrg?.settings.onboarding ?? null;
  const addFirstSiteComplete = sites.length > 0;
  const runFirstScanComplete = latestCompletedScan !== null;
  const autoReviewedBecauseNoCriticalIssues =
    runFirstScanComplete &&
    (firstScanSummary?.issueCounts.critical ?? latestCompletedScan?.criticalIssues ?? 0) === 0;
  const reviewCriticalIssuesComplete =
    Boolean(onboarding?.reviewedCriticalIssuesAt) || autoReviewedBecauseNoCriticalIssues;
  const exportReportComplete = Boolean(onboarding?.exportedReportAt);
  const complianceExportEnabled = usageData?.limits.complianceExportEnabled !== false;
  const onboardingSteps = [
    {
      id: 'add-site',
      title: t('onboarding.steps.addSite.title'),
      body: addFirstSiteComplete
        ? t('onboarding.steps.addSite.complete')
        : t('onboarding.steps.addSite.pending'),
      complete: addFirstSiteComplete,
      actionLabel: addFirstSiteComplete ? t('onboarding.steps.addSite.actionComplete') : t('onboarding.steps.addSite.actionPending'),
      actionTo: addFirstSiteComplete ? '/sites' : '/sites/new',
    },
    {
      id: 'run-scan',
      title: t('onboarding.steps.runScan.title'),
      body: runFirstScanComplete
        ? t('onboarding.steps.runScan.complete')
        : t('onboarding.steps.runScan.pending'),
      complete: runFirstScanComplete,
      actionLabel: runFirstScanComplete ? t('onboarding.steps.runScan.actionComplete') : t('onboarding.steps.runScan.actionPending'),
      actionTo: latestCompletedScan
        ? `/scans/${latestCompletedScan.id}`
        : sites[0]
          ? `/sites/${sites[0].id}`
          : '/sites/new',
    },
    {
      id: 'review-critical',
      title: t('onboarding.steps.reviewCritical.title'),
      body: reviewCriticalIssuesComplete
        ? t('onboarding.steps.reviewCritical.complete')
        : t('onboarding.steps.reviewCritical.pending'),
      complete: reviewCriticalIssuesComplete,
      actionLabel: firstScanSummary ? t('onboarding.steps.reviewCritical.actionReady') : t('onboarding.steps.reviewCritical.actionWaiting'),
      actionTo: firstScanSummary?.deepLinks.scanDetailPath ?? (latestCompletedScan ? `/scans/${latestCompletedScan.id}` : '/dashboard'),
    },
    {
      id: 'export-report',
      title: complianceExportEnabled
        ? t('onboarding.steps.exportReport.titleEnabled')
        : t('onboarding.steps.exportReport.titleLocked'),
      body: exportReportComplete
        ? t('onboarding.steps.exportReport.complete')
        : complianceExportEnabled
          ? t('onboarding.steps.exportReport.pendingEnabled')
          : t('onboarding.steps.exportReport.pendingLocked'),
      complete: exportReportComplete,
      actionLabel: firstScanSummary ? t('onboarding.steps.exportReport.actionReady') : t('onboarding.steps.exportReport.actionWaiting'),
      actionTo: firstScanSummary?.deepLinks.siteCompliancePath ?? '/sites',
    },
  ];
  const onboardingComplete = onboardingSteps.every((step) => step.complete);
  const dismissedOnboarding = Boolean(onboarding?.dismissedAt) && !onboardingComplete;
  const firstScanFocusIssues = (firstScanSummary?.topIssues ?? [])
    .filter((issue) => issue.impact === 'critical')
    .slice(0, 3);
  const guidedFirstScanIssues = firstScanFocusIssues.length > 0
    ? firstScanFocusIssues
    : (firstScanSummary?.topIssues ?? []).slice(0, 3);
  const sitesNeedingAttentionCount = useMemo(() => {
    if (fleetHealth) {
      return fleetHealth.sites.filter((site) => site.state !== 'healthy').length;
    }

    const siteIds = new Set<string>();
    for (const entry of [...sitesWithCritical, ...unscannedSites, ...staleSites]) {
      siteIds.add(entry.siteId);
    }
    return siteIds.size;
  }, [fleetHealth, sitesWithCritical, staleSites, unscannedSites]);

  const topSitesNeedingAttention = useMemo(() => {
    if (fleetHealth?.sites?.length) {
      return fleetHealth.sites
        .filter((site) => site.state !== 'healthy')
        .slice(0, 5)
        .map((site) => ({
          id: site.siteId,
          name: site.siteName,
          summary: fleetSiteSummaryLabel(site, t),
          stateLabel: healthStateLabel(site.state, t),
          stateVariant: healthStateVariant(site.state),
          sitePath: site.links.sitePath,
          secondaryPath: site.links.compliancePath,
          secondaryLabel: t('fleetHealth.compliance'),
        }));
    }

    const merged = [...sitesWithCritical, ...unscannedSites, ...staleSites];
    const seen = new Set<string>();
    return merged.filter((entry) => {
      if (seen.has(entry.siteId)) return false;
      seen.add(entry.siteId);
      return true;
    }).slice(0, 5).map((entry) => ({
      id: entry.siteId,
      name: entry.siteName,
      summary: entry.scan === null
        ? t('recommendation.firstScanBody', { count: 1 })
        : entry.scan.status === 'completed'
          ? t('recommendation.staleBody', { count: 1 })
          : t('recommendation.criticalBody', { siteName: entry.siteName }),
      stateLabel: entry.scan === null ? t('metrics.unscannedSites') : t('metrics.criticalIssues'),
      stateVariant: entry.scan === null ? 'warning' as BadgeVariant : 'danger' as BadgeVariant,
      sitePath: `/sites/${entry.siteId}`,
      secondaryPath: entry.scan ? `/scans/${entry.scan.id}` : `/sites/${entry.siteId}`,
      secondaryLabel: entry.scan ? t('recentScanTable.details') : t('recentScanTable.runScan'),
    }));
  }, [fleetHealth, sitesWithCritical, staleSites, t, unscannedSites]);

  const dashboardAttentionItems = useMemo(() => {
    const items: Array<{ id: string; label: string; body: string; to: string; tone: BadgeVariant }> = [];

    if (totalCritical > 0 && sitesWithCritical[0]) {
      items.push({
        id: 'critical',
        label: t('metrics.criticalIssues'),
        body: t('recommendation.criticalBody', { siteName: sitesWithCritical[0].siteName }),
        to: `/sites/${sitesWithCritical[0].siteId}`,
        tone: 'danger',
      });
    }

    if (activeScans.length > 0) {
      items.push({
        id: 'active-scans',
        label: t('metrics.activeScans'),
        body: t('recommendation.activeScansBody'),
        to: '/sites',
        tone: 'info',
      });
    }

    if (unscannedSites.length > 0 && unscannedSites[0]) {
      items.push({
        id: 'unscanned',
        label: t('metrics.unscannedSites'),
        body: t('recommendation.firstScanBody', { count: unscannedSites.length }),
        to: `/sites/${unscannedSites[0].siteId}`,
        tone: 'warning',
      });
    }

    if (fleetHealth?.counts.authRevalidationNeeded) {
      items.push({
        id: 'auth',
        label: t('fleetHealth.authRevalidation'),
        body: t('sections.fleetHealthSubtitle'),
        to: '/sites',
        tone: 'danger',
      });
    }

    return items.slice(0, 4);
  }, [activeScans.length, fleetHealth?.counts.authRevalidationNeeded, sitesWithCritical, t, totalCritical, unscannedSites]);

  const patchWorkspaceOnboarding = async (
    partial: NonNullable<Organisation['settings']['onboarding']>,
    feedback?: string,
  ) => {
    if (!org?.slug) return;

    setSavingOnboarding(true);
    try {
      const response = await api.patch<OrganisationResponse>(`/orgs/${org.slug}`, {
        settings: {
          onboarding: partial,
        },
      });
      setWorkspaceOrg(response.organisation);
      if (feedback) {
        setOnboardingFeedback({ message: feedback, variant: 'success' });
      }
    } catch {
      if (feedback) {
        setOnboardingFeedback({ message: t('onboarding.feedbackFailed'), variant: 'warning' });
      }
    } finally {
      setSavingOnboarding(false);
    }
  };

  const handleDismissOnboarding = () => {
    setOnboardingVisible(false);
    setOnboardingFeedback(null);
    void patchWorkspaceOnboarding(
      {
        dismissedAt: new Date().toISOString(),
      },
    );
  };

  const handleResumeOnboarding = () => {
    setOnboardingVisible(true);
    setOnboardingFeedback(null);
    void patchWorkspaceOnboarding({
      dismissedAt: null,
    });
  };

  const handleOpenCriticalReview = async (path: string) => {
    if (!reviewCriticalIssuesComplete) {
      await patchWorkspaceOnboarding(
        {
          reviewedCriticalIssuesAt: new Date().toISOString(),
        },
        t('onboarding.feedbackReviewed'),
      );
    }
    navigate(path);
  };

  useEffect(() => {
    if (!onboardingComplete || onboarding?.completedAt || savingOnboarding || !org?.slug) {
      return;
    }

    void patchWorkspaceOnboarding({
      completedAt: new Date().toISOString(),
      dismissedAt: null,
    }, t('onboarding.feedbackFinished'));
  }, [onboardingComplete, onboarding?.completedAt, org?.slug, savingOnboarding, t]);

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------

  return (
    <Container maxWidth={1360}>
      <div className="acc-dashboard-surface">

        {/* ── Zone A: Hero ───────────────────────────────────────── */}
        <div className="acc-dashboard-surface__hero">
          <PageHeader
            eyebrow={t('hero.eyebrow')}
            title={t('hero.title')}
            subtitle={
              <span>
                {org?.name ?? t('hero.subtitleFallback')}
                {user?.fullName ? ` · ${t('hero.welcomeBack', { name: user.fullName.split(' ')[0] })}` : ''}
              </span>
            }
            description={t('hero.description')}
            meta={
              <>
                {planName && (
                  <Badge variant="info" style={{ textTransform: 'capitalize' }}>
                    {t('hero.planBadge', { plan: planName })}
                  </Badge>
                )}
                <Badge variant={totalCritical > 0 ? 'danger' : 'success'}>
                  {totalCritical > 0 ? t('hero.criticalIssuesOpen', { count: totalCritical }) : t('hero.noCriticalIssues')}
                </Badge>
                <Badge variant={activeScans.length > 0 ? 'warning' : 'neutral'}>
                  {activeScans.length > 0 ? t('hero.activeScans', { count: activeScans.length }) : t('hero.noActiveScans')}
                </Badge>
              </>
            }
            action={
              <>
                <Link
                  to="/sites"
                  className="acc-link-button"
                >
                  {t('common:navigation.openSitePortfolio')}
                </Link>
                <Link
                  to="/alerts"
                  className="acc-link-button"
                >
                  {t('common:navigation.reviewAlerts')}
                </Link>
              </>
            }
          />
        </div>

        <div className="acc-command-toolbar-wrap">
          <Card
            className="acc-command-toolbar"
            style={{
              padding: '14px 16px',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 14,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <div>
                <div style={{ fontSize: 10.5, fontWeight: 760, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>
                  {t('executive.quickLinksLabel')}
                </div>
                <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.45 }}>
                  {t('common:manualIssues.positioning')}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Link to="/sites" style={{ textDecoration: 'none' }}>
                  <Button variant="secondary" size="sm">{t('common:navigation.openSitePortfolio')}</Button>
                </Link>
                <Link to="/alerts" style={{ textDecoration: 'none' }}>
                  <Button variant="secondary" size="sm">{t('common:navigation.reviewAlerts')}</Button>
                </Link>
                <Link to="/workspace" style={{ textDecoration: 'none' }}>
                  <Button variant="secondary" size="sm">{t('common:navigation.workspace')}</Button>
                </Link>
                <Link to="/billing" style={{ textDecoration: 'none' }}>
                  <Button variant="secondary" size="sm">{t('common:navigation.billing')}</Button>
                </Link>
              </div>
            </div>
          </Card>
        </div>

        {!loading && (
          <div className="acc-dashboard-surface__summary">
            <div className="acc-command-kpis">
              <Card className="acc-dashboard-score-card" style={{ padding: '18px 20px' }}>
                <div style={{ fontSize: 10.5, fontWeight: 760, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                  {t('scoreSummary.title')}
                </div>
                {orgScore !== null ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
                    <ScoreCard score={orgScore} size="sm" />
                    <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                      {t('scoreSummary.average', { count: scannedResults.length })}
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 28, fontWeight: 800, color: C.muted }}>—</div>
                )}
              </Card>
              <StatCard
                value={totalCritical}
                label={t('metrics.criticalIssues')}
                hint={t('metrics.criticalIssuesHint')}
                accent={totalCritical > 0 ? C.danger : undefined}
              />
              <StatCard
                value={sitesNeedingAttentionCount}
                label={t('metrics.sitesNeedingAttention')}
                hint={fleetSummaryLabel(fleetHealth, t)}
                accent={sitesNeedingAttentionCount > 0 ? C.warning : undefined}
              />
              <StatCard
                value={activeScans.length}
                label={t('metrics.scansInProgress')}
                hint={activeScans.length > 0 ? t('hero.activeScans', { count: activeScans.length }) : t('hero.noActiveScans')}
                accent={activeScans.length > 0 ? 'var(--acc-color-primary)' : undefined}
              />
            </div>

            <div className="acc-dashboard-surface__overview">
              <Card className="acc-command-card">
                {!sites.length ? (
                  <>
                    <SectionHeader
                      eyebrow={t('onboarding.eyebrow')}
                      title={t('onboarding.title')}
                      subtitle={t('onboarding.description')}
                    />
                    <EmptyState
                      dashed
                      title={t('emptyState.title')}
                      description={t('emptyState.description')}
                      action={
                        <Button onClick={() => navigate('/sites/new')}>
                          {t('emptyState.cta')}
                        </Button>
                      }
                    />
                  </>
                ) : !onboardingComplete && onboardingVisible ? (
                  <>
                    <SectionHeader
                      eyebrow={t('onboarding.eyebrow')}
                      title={t('onboarding.title')}
                      subtitle={t('onboarding.description')}
                      action={
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Badge variant="info">
                            {t('onboarding.progress', {
                              complete: onboardingSteps.filter((step) => step.complete).length,
                              total: onboardingSteps.length,
                            })}
                          </Badge>
                          <Button variant="secondary" size="sm" onClick={handleDismissOnboarding}>
                            {t('onboarding.dismiss')}
                          </Button>
                        </div>
                      }
                    />
                    {onboardingFeedback && (
                      <AlertBanner variant={onboardingFeedback.variant} style={{ marginBottom: 14 }}>
                        {onboardingFeedback.message}
                      </AlertBanner>
                    )}
                    <div style={{ display: 'grid', gap: 10 }}>
                      {onboardingSteps.map((step, index) => (
                        <div
                          key={step.id}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(0, 1fr) auto',
                            gap: 12,
                            border: `1px solid ${step.complete ? C.success : C.border}`,
                            borderRadius: 14,
                            background: step.complete ? C.successSoft : 'var(--card-bg)',
                            padding: '12px 14px',
                            alignItems: 'center',
                          }}
                        >
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                              <span style={{ fontSize: 10.5, fontWeight: 760, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                {t('onboarding.stepLabel', { count: index + 1 })}
                              </span>
                              <Badge variant={step.complete ? 'success' : 'neutral'}>
                                {step.complete ? t('onboarding.complete') : t('common:inviteStatuses.pending')}
                              </Badge>
                            </div>
                            <div style={{ fontSize: 14.5, fontWeight: 760, color: C.text, marginBottom: 4 }}>
                              {step.title}
                            </div>
                            <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.55 }}>
                              {step.body}
                            </div>
                          </div>
                          <div>
                            {step.id === 'review-critical' ? (
                              <Button variant="secondary" size="sm" onClick={() => void handleOpenCriticalReview(step.actionTo)}>
                                {step.actionLabel}
                              </Button>
                            ) : (
                              <Link to={step.actionTo} style={{ textDecoration: 'none' }}>
                                <Button variant="secondary" size="sm">{step.actionLabel}</Button>
                              </Link>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : dismissedOnboarding && !onboardingVisible ? (
                  <SectionHeader
                    eyebrow={t('onboarding.eyebrow')}
                    title={t('onboarding.resume')}
                    subtitle={t('onboarding.resumeDescription', {
                      complete: onboardingSteps.filter((step) => step.complete).length,
                      total: onboardingSteps.length,
                    })}
                    action={
                      <Button variant="secondary" size="sm" onClick={handleResumeOnboarding}>
                        {t('onboarding.resume')}
                      </Button>
                    }
                  />
                ) : firstScanSummary && runFirstScanComplete && (!reviewCriticalIssuesComplete || !exportReportComplete) ? (
                  <>
                    <SectionHeader
                      eyebrow={t('firstScan.eyebrow')}
                      title={t('firstScan.title')}
                      subtitle={t('firstScan.summary', {
                        siteName: firstScanSummary.site.name,
                        pages: firstScanSummary.scan.pagesScanned,
                        issues: firstScanSummary.scan.totalIssues,
                      })}
                      action={
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <Button onClick={() => void handleOpenCriticalReview(firstScanSummary.deepLinks.scanDetailPath)}>
                            {t('firstScan.cta')}
                          </Button>
                          <Link to={firstScanSummary.deepLinks.siteCompliancePath} style={{ textDecoration: 'none' }}>
                            <Button variant="secondary" size="sm">{t('firstScan.openReport')}</Button>
                          </Link>
                        </div>
                      }
                    />
                    <div style={{ display: 'grid', gap: 10 }}>
                      {guidedFirstScanIssues.length > 0 ? guidedFirstScanIssues.map((issue, index) => (
                        <div key={issue.issueId} className="acc-summary-list__row">
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                              {t('firstScan.priority', { count: index + 1 })}
                            </div>
                            <div style={{ fontWeight: 700, color: C.text, fontSize: 14, marginBottom: 4 }}>
                              {issue.title}
                            </div>
                            <div className="acc-summary-list__meta">
                              {t('firstScan.occurrences', {
                                occurrences: issue.occurrences,
                                pages: issue.affectedPages,
                              })}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <Badge variant={issue.impact === 'critical' ? 'danger' : 'warning'}>
                              {t(`reports:labels.severity.${issue.impact}` as const, { defaultValue: issue.impact })}
                            </Badge>
                            <Badge variant="neutral">{formatWorkflowState(issue.workflowState, t)}</Badge>
                          </div>
                        </div>
                      )) : (
                        <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.7 }}>
                          {t('firstScan.noCritical')}
                        </div>
                      )}
                    </div>
                  </>
                ) : recommendation ? (
                  <>
                    <SectionHeader
                      eyebrow={t('executive.priorityTitle')}
                      title={recommendation.title}
                      subtitle={recommendation.body}
                      action={
                        recommendation.to && recommendation.cta ? (
                          <Link to={recommendation.to} style={{ textDecoration: 'none' }}>
                            <Button>{recommendation.cta}</Button>
                          </Link>
                        ) : undefined
                      }
                    />
                    <RecommendationPanel
                      icon={recommendation.icon}
                      title={recommendation.title}
                      body={recommendation.body}
                      variant={recommendation.variant}
                    />
                  </>
                ) : (
                  <AlertBanner variant="success">
                    {t('recommendation.allGoodBody')}
                  </AlertBanner>
                )}
              </Card>

              <Card className="acc-command-card acc-command-card--priority">
                <SectionHeader
                  eyebrow={t('executive.attentionTitle')}
                  title={t('executive.attentionTitle')}
                  subtitle={t('executive.attentionSubtitle')}
                />
                {dashboardAttentionItems.length === 0 ? (
                  <AlertBanner variant="success">
                    {t('executive.attentionHealthy')}
                  </AlertBanner>
                ) : (
                  <div className="acc-summary-list">
                    {dashboardAttentionItems.map((item) => (
                      <div key={item.id} className="acc-summary-list__row">
                        <div style={{ minWidth: 0 }}>
                          <div style={{ marginBottom: 4 }}>
                            <Badge variant={item.tone}>{item.label}</Badge>
                          </div>
                          <div className="acc-summary-list__meta">{item.body}</div>
                        </div>
                        <Link to={item.to} style={{ color: C.link, textDecoration: 'none', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {t('executive.viewDetails')}
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </div>
        )}

        {!loading && workspaceOrg && onboardingComplete && (
          <div style={{ marginBottom: 24 }}>
            <AlertBanner variant="success">
              <strong>{t('onboarding.feedbackComplete')}</strong>
            </AlertBanner>
          </div>
        )}

        {!loading && sites.length > 0 && atPlanLimit && (
          <AlertBanner variant="danger" icon="⚠" style={{ marginBottom: 20 }}>
            <strong>{t('planLimitAlert.title')}</strong>{' '}
            {t('planLimitAlert.body', {
              kind: scansAtLimit ? t('planUsage.scansThisMonth') : t('planUsage.sites'),
            })}
          </AlertBanner>
        )}

        {!loading && sites.length > 0 && (
          <div className="acc-dashboard-surface__secondary">
            <div style={{ display: 'grid', gap: 20 }}>
              <Card className="acc-command-card">
                <SectionHeader
                  title={t('sections.recentAlertsTitle')}
                  subtitle={t('sections.recentAlertsSubtitle')}
                  action={(
                    <Link to="/alerts" style={{ color: C.link, fontSize: 13, textDecoration: 'none', fontWeight: 600 }}>
                      {t('common:actions.viewAll')}
                    </Link>
                  )}
                />
                {recentAlerts.filter((a) => !a.isRead || a.severity === 'critical').slice(0, 4).length === 0 ? (
                  <AlertBanner variant="success">{t('executive.alertsClear')}</AlertBanner>
                ) : (
                  <div className="acc-summary-list">
                    {recentAlerts.filter((a) => !a.isRead || a.severity === 'critical').slice(0, 4).map((alert) => (
                      <div key={alert.id} className="acc-summary-list__row">
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                            <Badge variant={alert.severity === 'critical' ? 'danger' : alert.severity === 'warning' ? 'warning' : 'info'}>
                              {alertSeverityLabel(alert.severity, t)}
                            </Badge>
                            <span style={{ fontWeight: 700, color: C.text, fontSize: 14 }}>{alert.title}</span>
                          </div>
                          <div className="acc-summary-list__meta">{alert.message}</div>
                        </div>
                        <div style={{ textAlign: 'right', minWidth: 110 }}>
                          <div className="acc-summary-list__meta" style={{ marginBottom: 6 }}>
                            {formatDate(alert.createdAt) || t('common:labels.notAvailable')}
                          </div>
                          <Link to="/alerts" style={{ color: C.link, textDecoration: 'none', fontSize: 12, fontWeight: 700 }}>
                            {t('executive.viewDetails')}
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <Card>
                <SectionHeader
                  title={t('sections.recentScanActivityTitle')}
                  subtitle={t('sections.recentScanActivitySubtitle')}
                  action={(
                    <Link to="/sites" style={{ color: C.link, fontSize: 13, textDecoration: 'none', fontWeight: 600 }}>
                      {t('sitesTable.viewAll')}
                    </Link>
                  )}
                />
                {recentScans.length === 0 ? (
                  <EmptyState
                    dashed
                    title={t('recentScanTable.empty')}
                    description={t('emptyState.description')}
                    action={
                      <Button onClick={() => navigate('/sites/new')}>
                        {t('recentScanTable.addFirstSite')}
                      </Button>
                    }
                  />
                ) : (
                  <div className="acc-summary-list">
                    {recentScans.slice(0, 5).map(({ siteId, siteName, scan }) => {
                      const score = scan && (scan.status === 'completed' || scan.status === 'completed_with_failures')
                        ? estimateAccessibilityScore(scan.criticalIssues ?? 0, scan.totalIssues ?? 0)
                        : null;

                      return (
                        <div key={siteId} className="acc-summary-list__row">
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                              <Link to={`/sites/${siteId}`} style={{ color: C.text, textDecoration: 'none', fontWeight: 700, fontSize: 14 }}>
                                {siteName}
                              </Link>
                              {scan ? (
                                <Badge variant={statusVariant(scan.status)}>{statusLabel(scan.status, t)}</Badge>
                              ) : (
                                <Badge variant="warning">{t('recentScanTable.neverScanned')}</Badge>
                              )}
                              {score !== null && <Badge variant={getScoreVariant(score)}>{score}</Badge>}
                            </div>
                            <div className="acc-summary-list__meta">
                              {scan
                                ? `${formatNumber(scan.pagesScanned)} ${t('recentScanTable.pages').toLowerCase()} · ${formatNumber(scan.criticalIssues)} ${t('recentScanTable.critical').toLowerCase()} · ${formatNumber(scan.totalIssues)} ${t('recentScanTable.totalIssues').toLowerCase()}`
                                : t('recommendation.firstScanBody', { count: 1 })}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', minWidth: 120 }}>
                            <div className="acc-summary-list__meta" style={{ marginBottom: 6 }}>
                              {scan?.completedAt
                                ? formatDate(scan.completedAt)
                                : scan?.createdAt
                                  ? formatDate(scan.createdAt)
                                  : t('common:labels.notAvailable')}
                            </div>
                            <Link
                              to={scan ? `/scans/${scan.id}` : `/sites/${siteId}`}
                              style={{ color: C.link, textDecoration: 'none', fontSize: 12, fontWeight: 700 }}
                            >
                              {scan ? t('recentScanTable.details') : t('recentScanTable.runScan')}
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            </div>

            <div style={{ display: 'grid', gap: 20 }}>
              {fleetHealth && fleetHealth.totalSites > 0 && (
                <Card>
                  <SectionHeader
                    title={t('sections.fleetHealthTitle')}
                    subtitle={t('sections.fleetHealthSubtitle')}
                    action={(
                      <Link to="/sites" style={{ color: C.link, fontSize: 13, textDecoration: 'none', fontWeight: 600 }}>
                        {t('fleetHealth.openSitePortfolio')}
                      </Link>
                    )}
                  />
                  <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>
                    {fleetSummaryLabel(fleetHealth, t)}
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                      gap: 10,
                      marginBottom: 16,
                    }}
                  >
                    {[
                      [t('fleetHealth.healthy'), fleetHealth.counts.healthy],
                      [t('fleetHealth.attentionNeeded'), fleetHealth.counts.attentionNeeded],
                      [t('fleetHealth.authRevalidation'), fleetHealth.counts.authRevalidationNeeded],
                      [t('fleetHealth.regression'), fleetHealth.counts.regressionDetected],
                    ].map(([label, count]) => (
                      <div key={label} style={{ border: '1px solid var(--acc-color-border)', borderRadius: 12, padding: '12px 14px', background: 'var(--acc-color-surface-muted)' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>
                          {label}
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: C.text }}>{count}</div>
                      </div>
                    ))}
                  </div>
                  <div className="acc-summary-list">
                    {fleetHealth.sites.slice(0, 3).map((site) => (
                      <div key={site.siteId} className="acc-summary-list__row">
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                            <Link to={site.links.sitePath} style={{ color: C.text, textDecoration: 'none', fontWeight: 700, fontSize: 14 }}>
                              {site.siteName}
                            </Link>
                            <Badge variant={healthStateVariant(site.state)}>
                              {healthStateLabel(site.state, t)}
                            </Badge>
                          </div>
                          <div className="acc-summary-list__meta">{fleetSiteSummaryLabel(site, t)}</div>
                        </div>
                        <Link to={site.links.compliancePath} style={{ color: C.link, textDecoration: 'none', fontSize: 12, fontWeight: 700 }}>
                          {t('fleetHealth.compliance')}
                        </Link>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              <Card>
                <SectionHeader
                  title={t('sections.topSitesTitle')}
                  subtitle={t('sections.topSitesSubtitle')}
                  action={(
                    <Link to="/sites" style={{ color: C.link, fontSize: 13, textDecoration: 'none', fontWeight: 600 }}>
                      {t('common:actions.manage')}
                    </Link>
                  )}
                />
                {topSitesNeedingAttention.length === 0 ? (
                  <AlertBanner variant="success">{t('executive.attentionHealthy')}</AlertBanner>
                ) : (
                  <div className="acc-summary-list">
                    {topSitesNeedingAttention.map((site) => (
                      <div key={site.id} className="acc-summary-list__row">
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                            <Link to={site.sitePath} style={{ color: C.text, textDecoration: 'none', fontWeight: 700, fontSize: 14 }}>
                              {site.name}
                            </Link>
                            <Badge variant={site.stateVariant}>{site.stateLabel}</Badge>
                          </div>
                          <div className="acc-summary-list__meta">{site.summary}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          <Link to={site.sitePath} style={{ color: C.link, textDecoration: 'none', fontSize: 12, fontWeight: 700 }}>
                            {t('fleetHealth.site')}
                          </Link>
                          <Link to={site.secondaryPath} style={{ color: C.link, textDecoration: 'none', fontSize: 12, fontWeight: 700 }}>
                            {site.secondaryLabel}
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {usageData && (
                <Card>
                  <SectionHeader
                    title={t('sections.commercialTitle')}
                    subtitle={t('sections.commercialSubtitle')}
                    action={(
                      <Link to="/billing" style={{ color: C.link, fontSize: 13, textDecoration: 'none', fontWeight: 600 }}>
                        {t('common:navigation.billing')}
                      </Link>
                    )}
                  />
                  {planName && (
                    <div style={{ marginBottom: 14 }}>
                      <Badge variant="info" style={{ textTransform: 'capitalize' }}>
                        {t('hero.planBadge', { plan: planName })}
                      </Badge>
                    </div>
                  )}
                  <QuotaBar used={usageData.usage.sitesCount} limit={usageData.limits.maxSites} label={t('planUsage.sites')} testId="dashboard-plan-sites" />
                  <QuotaBar used={usageData.usage.scansThisMonth} limit={usageData.limits.maxScansPerMonth} label={t('planUsage.scansThisMonth')} />
                  <div className="acc-summary-list">
                    <div className="acc-summary-list__row">
                      <div style={{ fontWeight: 700, color: C.text, fontSize: 14 }}>{t('sections.exportEntitlement')}</div>
                      <Badge variant={usageData.limits.complianceExportEnabled ? 'success' : 'warning'}>
                        {usageData.limits.complianceExportEnabled ? t('executive.enabled') : t('executive.upgradeRequired')}
                      </Badge>
                    </div>
                    <div className="acc-summary-list__row">
                      <div style={{ fontWeight: 700, color: C.text, fontSize: 14 }}>{t('sections.alertEntitlement')}</div>
                      <Badge variant={usageData.limits.scheduledScansEnabled ? 'success' : 'neutral'}>
                        {usageData.limits.scheduledScansEnabled ? t('executive.enabled') : t('executive.limited')}
                      </Badge>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </div>
        )}

      </div>
    </Container>
  );
}
